import React from "react";
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import { Box, Typography } from "@mui/material";
import { LoadingDialogConfig } from "../../redux/slice/commonSlice";

interface LoadingDialogProps {
    open: boolean;
    config?: LoadingDialogConfig | null;
}

const LoadingDialog: React.FC<LoadingDialogProps> = ({
    open,
    config,
}) => {
    const isAiMedicalAudit = config?.variant === "aiMedicalAudit";
    const orbitDots = [
        { top: "12%", left: "50%", delay: "0s", color: "#7dd3fc" },
        { top: "23%", left: "76%", delay: "0.15s", color: "#38bdf8" },
        { top: "50%", left: "88%", delay: "0.3s", color: "#22d3ee" },
        { top: "77%", left: "76%", delay: "0.45s", color: "#34d399" },
        { top: "88%", left: "50%", delay: "0.6s", color: "#facc15" },
        { top: "77%", left: "24%", delay: "0.75s", color: "#fb7185" },
        { top: "50%", left: "12%", delay: "0.9s", color: "#c084fc" },
        { top: "23%", left: "24%", delay: "1.05s", color: "#818cf8" },
    ];

    return (
        <Backdrop sx={{ color: '#fff', zIndex:(theme) => theme.zIndex.drawer + 1301 ,  backdropFilter: isAiMedicalAudit ? "blur(2px)" : undefined }} open={open} >
            {isAiMedicalAudit ? (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        maxWidth: 420,
                        px: 3,
                        py: 2,
                        textAlign: "center",
                    }}
                >
                    <Box
                        sx={{
                            position: "relative",
                            width: { xs: 170, sm: 200 },
                            height: { xs: 170, sm: 200 },
                            mb: 1.5,
                        }}
                    >
                        <Box
                            sx={{
                                position: "absolute",
                                inset: "18%",
                                borderRadius: "50%",
                                border: "1px solid rgba(125, 211, 252, 0.28)",
                                animation: "auditPulse 2.4s ease-in-out infinite",
                                "@keyframes auditPulse": {
                                    "0%, 100%": {
                                        transform: "scale(0.92)",
                                        opacity: 0.5,
                                    },
                                    "50%": {
                                        transform: "scale(1.05)",
                                        opacity: 1,
                                    },
                                },
                            }}
                        />
                        {orbitDots.map((dot, index) => (
                            <Box
                                key={`${dot.top}-${dot.left}-${index}`}
                                sx={{
                                    position: "absolute",
                                    top: dot.top,
                                    left: dot.left,
                                    width: 12,
                                    height: 12,
                                    borderRadius: "50%",
                                    backgroundColor: dot.color,
                                    boxShadow: `0 0 16px ${dot.color}`,
                                    transform: "translate(-50%, -50%)",
                                    animation: "auditDotPulse 1.6s ease-in-out infinite",
                                    animationDelay: dot.delay,
                                    "@keyframes auditDotPulse": {
                                        "0%, 100%": {
                                            transform: "translate(-50%, -50%) scale(0.8)",
                                            opacity: 0.45,
                                        },
                                        "50%": {
                                            transform: "translate(-50%, -50%) scale(1.25)",
                                            opacity: 1,
                                        },
                                    },
                                }}
                            />
                        ))}
                        <Box
                            sx={{
                                position: "absolute",
                                inset: "31%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: "28px",
                                background: "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(8, 47, 73, 0.92))",
                                border: "1px solid rgba(56, 189, 248, 0.35)",
                                boxShadow: "0 18px 40px rgba(8, 145, 178, 0.24)",
                                overflow: "hidden",
                            }}
                        >
                            <Box
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    background: "linear-gradient(120deg, transparent 0%, rgba(125, 211, 252, 0.18) 48%, transparent 100%)",
                                    transform: "translateX(-130%)",
                                    animation: "auditSweep 1.8s linear infinite",
                                    "@keyframes auditSweep": {
                                        "0%": {
                                            transform: "translateX(-130%)",
                                        },
                                        "100%": {
                                            transform: "translateX(130%)",
                                        },
                                    },
                                }}
                            />
                            <Box
                                sx={{
                                    position: "relative",
                                    width: "62%",
                                    height: "72%",
                                    borderRadius: "14px",
                                    background: "linear-gradient(180deg, rgba(224, 242, 254, 0.98), rgba(186, 230, 253, 0.9))",
                                    boxShadow: "0 12px 30px rgba(8, 145, 178, 0.16)",
                                }}
                            >
                                <Box
                                    sx={{
                                        position: "absolute",
                                        top: 14,
                                        left: "18%",
                                        right: "18%",
                                        height: 4,
                                        borderRadius: 999,
                                        backgroundColor: "rgba(14, 116, 144, 0.18)",
                                    }}
                                />
                                <Box
                                    sx={{
                                        position: "absolute",
                                        top: 28,
                                        left: "18%",
                                        right: "28%",
                                        height: 4,
                                        borderRadius: 999,
                                        backgroundColor: "rgba(14, 116, 144, 0.18)",
                                    }}
                                />
                                <Box
                                    sx={{
                                        position: "absolute",
                                        bottom: 18,
                                        left: "18%",
                                        right: "18%",
                                        height: 10,
                                        borderRadius: 999,
                                        background: "linear-gradient(90deg, #06b6d4, #38bdf8, #34d399)",
                                        backgroundSize: "200% 100%",
                                        animation: "auditProgress 1.8s linear infinite",
                                        "@keyframes auditProgress": {
                                            "0%": { backgroundPosition: "0% 50%" },
                                            "100%": { backgroundPosition: "100% 50%" },
                                        },
                                    }}
                                />
                                <Box
                                    sx={{
                                        position: "absolute",
                                        top: "44%",
                                        left: "18%",
                                        right: "18%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                    }}
                                >
                                    {[0, 1, 2].map((index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                width: 16,
                                                height: 16,
                                                borderRadius: "50%",
                                                backgroundColor: "#0ea5e9",
                                                animation: "auditBeat 1.2s ease-in-out infinite",
                                                animationDelay: `${index * 0.18}s`,
                                                "@keyframes auditBeat": {
                                                    "0%, 100%": { transform: "scale(0.72)", opacity: 0.45 },
                                                    "50%": { transform: "scale(1)", opacity: 1 },
                                                },
                                            }}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                        {config?.title || "Preparing your medical note audit"}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.82)" }}>
                        {config?.message || "We are securely uploading the document and generating audit results. This short wait is expected."}
                    </Typography>
                </Box>
            ) : (
                <CircularProgress size={60} variant="indeterminate" sx={{color:'#13fafa'}}/>
            )}
        </Backdrop>
    );
};

export default LoadingDialog;
